import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "availableItems",
    "navigationBreadcrumb",
    "selectedGearSection"
  ]

  static values = {
    currentStep: String,
    groupCount: { type: Number, default: 4 },
    initialCategoryCounts: Object,
    initialGroupCounts: Object
  }

  connect() {
    this.boundTurboFrameRenderHandler = this.handleTurboStreamUpdate.bind(this)

    this.initializeCollapsedGroups()
    this.collapseAllCategories()
    this.renderSelectionBadges(this.initialCategoryCounts(), this.initialGroupCounts())

    this.setupScrollListener()
    this.updateNavigationBreadcrumb()

    document.addEventListener("turbo:frame-render", this.boundTurboFrameRenderHandler)

    this.setupGearObserver()
    this.updateSelectionBadges()
  }

  disconnect() {
    this.removeScrollListener()

    if (this.gearObserver) {
      this.gearObserver.disconnect()
    }

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
    }

    if (this.zoeticThrottle) {
      clearTimeout(this.zoeticThrottle)
    }

    if (this.boundTurboFrameRenderHandler) {
      document.removeEventListener("turbo:frame-render", this.boundTurboFrameRenderHandler)
    }
  }

  setupScrollListener() {
    if (!this.hasAvailableItemsTarget) return

    this.scrollHandler = this.updateNavigationBreadcrumb.bind(this)
    this.availableItemsTarget.addEventListener("scroll", this.scrollHandler)
  }

  removeScrollListener() {
    if (!this.hasAvailableItemsTarget || !this.scrollHandler) return

    this.availableItemsTarget.removeEventListener("scroll", this.scrollHandler)
  }

  initializeCollapsedGroups() {
    for (let groupIndex = 0; groupIndex < this.groupCountValue; groupIndex += 1) {
      const contentElement = this.groupContentElement(groupIndex)
      const chevronElement = this.groupChevronElement(groupIndex)
      const button = this.groupButtonElement(groupIndex)

      if (!contentElement || !chevronElement || !button) continue

      contentElement.classList.add("hidden")
      contentElement.style.maxHeight = "0px"
      chevronElement.style.transform = "rotate(-90deg)"
      button.setAttribute("aria-expanded", "false")
    }
  }

  updateNavigationBreadcrumb() {
    if (!this.hasAvailableItemsTarget || !this.hasNavigationBreadcrumbTarget) return

    const sections = this.availableItemsTarget.querySelectorAll("[data-group]")
    let currentSection = null

    sections.forEach((section, index) => {
      const rect = section.getBoundingClientRect()
      const containerRect = this.availableItemsTarget.getBoundingClientRect()

      if (rect.top <= containerRect.top + 100) {
        currentSection = {
          index,
          name: section.querySelector("button")?.textContent?.trim().split("(")[0].trim()
        }
      }
    })

    if (!currentSection?.name) {
      this.navigationBreadcrumbTarget.classList.add("hidden")
      this.navigationBreadcrumbTarget.replaceChildren()
      return
    }

    this.navigationBreadcrumbTarget.classList.remove("hidden")
    this.navigationBreadcrumbTarget.replaceChildren(this.navigationBreadcrumbContent(currentSection))
  }

  navigationBreadcrumbContent(currentSection) {
    const wrapper = document.createElement("div")
    wrapper.className = "flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded border-b"

    const label = document.createElement("span")
    label.className = "text-sm text-blue-800 dark:text-blue-300"
    label.append("Currently viewing: ")

    const strong = document.createElement("strong")
    strong.textContent = currentSection.name
    label.append(strong)

    const button = document.createElement("button")
    button.type = "button"
    button.className = "text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
    button.dataset.action = "click->gear-selection#collapseCurrentSection"
    button.dataset.sectionIndex = currentSection.index
    button.textContent = "Collapse Section"

    wrapper.append(label, button)
    return wrapper
  }

  collapseCurrentSection(event) {
    const sectionIndex = event.currentTarget.dataset.sectionIndex
    if (sectionIndex === undefined) return

    const button = this.groupButtonElement(sectionIndex)
    if (button) {
      this.toggleGroup({ currentTarget: button, target: button })
    }
  }

  toggleGroup(event) {
    const button = event.currentTarget || event.target.closest("[data-group]")
    const groupIndex = button?.dataset.group
    if (groupIndex === undefined) return

    const contentElement = this.groupContentElement(groupIndex)
    const chevronElement = this.groupChevronElement(groupIndex)
    if (!contentElement || !chevronElement) return

    const isExpanded = !contentElement.classList.contains("hidden")

    if (isExpanded) {
      this.collapseGroup(groupIndex)
      return
    }

    this.collapseAllGroups()
    this.collapseAllCategories()

    contentElement.classList.remove("hidden")
    contentElement.style.maxHeight = "none"
    chevronElement.style.transform = "rotate(0deg)"
    button.setAttribute("aria-expanded", "true")
  }

  collapseGroup(groupIndex) {
    const contentElement = this.groupContentElement(groupIndex)
    const chevronElement = this.groupChevronElement(groupIndex)
    const button = this.groupButtonElement(groupIndex)

    if (contentElement) {
      contentElement.style.maxHeight = "0px"
      contentElement.classList.add("hidden")
    }

    if (chevronElement) {
      chevronElement.style.transform = "rotate(-90deg)"
    }

    if (button) {
      button.setAttribute("aria-expanded", "false")
    }
  }

  collapseAllGroups() {
    for (let groupIndex = 0; groupIndex < this.groupCountValue; groupIndex += 1) {
      this.collapseGroup(groupIndex)
    }
  }

  toggleCategory(event) {
    const button = event.currentTarget || event.target.closest("[data-category]")
    const category = button?.dataset.category
    if (!category) return

    const contentElement = this.categoryContentElement(category)
    const chevronElement = this.categoryChevronElement(category)
    if (!contentElement || !chevronElement) return

    const isExpanded = !contentElement.classList.contains("hidden")

    if (isExpanded) {
      this.collapseCategory(category)
      return
    }

    this.collapseAllCategories()
    contentElement.classList.remove("hidden")
    chevronElement.style.transform = "rotate(90deg)"
    button.setAttribute("aria-expanded", "true")
  }

  collapseCategory(category) {
    const contentElement = this.categoryContentElement(category)
    const chevronElement = this.categoryChevronElement(category)
    const button = this.categoryButtonElement(category)

    if (contentElement) {
      contentElement.classList.add("hidden")
    }

    if (chevronElement) {
      chevronElement.style.transform = "rotate(0deg)"
    }

    if (button) {
      button.setAttribute("aria-expanded", "false")
    }
  }

  collapseAllCategories() {
    this.element.querySelectorAll("[data-gear-selection-target^='categoryContent']").forEach((contentElement) => {
      const targetName = contentElement.getAttribute("data-gear-selection-target")
      const category = targetName.replace("categoryContent", "")
      this.collapseCategory(category)
    })
  }

  updateZoeticPotentialDisplayThrottled() {
    if (this.zoeticThrottle) {
      clearTimeout(this.zoeticThrottle)
    }

    this.zoeticThrottle = setTimeout(() => {
      this.updateZoeticPotentialDisplay()
    }, 500)
  }

  updateZoeticPotentialDisplay() {
    const zoeticDisplay = document.getElementById("zoetic_potential_display")
    if (!zoeticDisplay || this.zoeticUpdateInProgress) return

    this.zoeticUpdateInProgress = true

    const characterId = window.location.pathname.match(/\/characters\/(\d+)/)?.[1]
    if (!characterId) {
      this.zoeticUpdateInProgress = false
      return
    }

    fetch(`/characters/${characterId}/zoetic_potential`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.zoetic_potential !== undefined) {
          zoeticDisplay.textContent = data.zoetic_potential
        }
      })
      .catch((error) => {
        console.error("Error updating Zoetic Potential:", error)
      })
      .finally(() => {
        this.zoeticUpdateInProgress = false
      })
  }

  updateSelectionBadges() {
    if (!this.hasSelectedGearSectionTarget) return

    const categoryCounts = {}
    const selectedItems = this.selectedGearSectionTarget.querySelectorAll("[data-gear-category]")

    selectedItems.forEach((item) => {
      const category = item.dataset.gearCategory
      if (!category) return

      const categoryKey = category.toLowerCase().replace(/[^a-z0-9]/gi, "_")
      categoryCounts[categoryKey] = (categoryCounts[categoryKey] || 0) + 1
    })

    this.renderSelectionBadges(categoryCounts, this.groupCountsFromCategories(categoryCounts))
    this.updateItemIndicators()
    this.updateZoeticPotentialDisplayThrottled()
  }

  renderSelectionBadges(categoryCounts, groupCounts) {
    this.element.querySelectorAll("[data-gear-selection-target^='categorySelectionBadge']").forEach((badge) => {
      const categoryKey = badge.getAttribute("data-gear-selection-target").replace("categorySelectionBadge", "")
      this.renderBadge(badge, categoryCounts[categoryKey] || 0)
    })

    this.element.querySelectorAll("[data-gear-selection-target^='groupSelectionBadge']").forEach((badge) => {
      const groupIndex = parseInt(badge.getAttribute("data-gear-selection-target").replace("groupSelectionBadge", ""), 10)
      this.renderBadge(badge, groupCounts[groupIndex] || 0)
    })
  }

  renderBadge(badge, count) {
    if (count > 0) {
      badge.textContent = count
      badge.classList.remove("hidden")
      badge.classList.add("inline-flex")
      return
    }

    badge.textContent = "0"
    badge.classList.add("hidden")
    badge.classList.remove("inline-flex")
  }

  groupCountsFromCategories(categoryCounts) {
    const groupCounts = {}

    Object.entries(this.categoryGroups()).forEach(([groupIndex, categories]) => {
      groupCounts[groupIndex] = categories.reduce((total, categoryKey) => total + (categoryCounts[categoryKey] || 0), 0)
    })

    return groupCounts
  }

  handleTurboStreamUpdate() {
    this.updateSelectionBadges()
  }

  setupGearObserver() {
    if (!this.hasSelectedGearSectionTarget) return

    this.gearObserver = new MutationObserver(() => {
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout)
      }

      this.updateTimeout = setTimeout(() => this.updateSelectionBadges(), 50)
    })

    this.gearObserver.observe(this.selectedGearSectionTarget, {
      childList: true,
      subtree: true
    })
  }

  updateItemIndicators() {
    if (!this.hasSelectedGearSectionTarget || !this.hasAvailableItemsTarget) return

    const selectedItems = {}
    const selectedGearItems = this.selectedGearSectionTarget.querySelectorAll("[data-gear-category]")

    selectedGearItems.forEach((item) => {
      const itemName = item.querySelector(".font-medium")?.textContent?.trim()
      if (!itemName) return

      selectedItems[itemName] = (selectedItems[itemName] || 0) + 1
    })

    this.availableItemsTarget.querySelectorAll("[data-gear-item-card]").forEach((card) => {
      const itemName = card.dataset.gearItemName
      if (!itemName) return

      const selectedCount = selectedItems[itemName] || 0
      this.renderItemIndicator(card, selectedCount)
    })
  }

  renderItemIndicator(card, selectedCount) {
    const indicator = card.querySelector("[data-gear-selection-indicator]")
    const addButton = card.querySelector("[data-gear-add-button]")
    if (!indicator || !addButton) return

    if (selectedCount > 0) {
      indicator.classList.remove("hidden")
      indicator.textContent = selectedCount > 1 ? `${selectedCount}` : "✓"
      addButton.textContent = "+"
      addButton.title = "Add Another"
      addButton.classList.remove("bg-red-600", "hover:bg-red-700", "focus:ring-red-500", "bg-blue-600", "hover:bg-blue-700", "focus:ring-blue-500", "bg-purple-600", "hover:bg-purple-700", "focus:ring-purple-500", "bg-gray-600", "hover:bg-gray-700", "focus:ring-gray-500")
      addButton.classList.add("bg-green-600", "hover:bg-green-700", "focus:ring-green-500")
      return
    }

    indicator.classList.add("hidden")
    indicator.textContent = ""
    addButton.textContent = "Add"
    addButton.title = "Add to gear"
    addButton.classList.remove("bg-green-600", "hover:bg-green-700", "focus:ring-green-500")

    const color = addButton.dataset.defaultColor || "gray"
    addButton.classList.add(`bg-${color}-600`, `hover:bg-${color}-700`, `focus:ring-${color}-500`)
  }

  categoryGroups() {
    switch (this.currentStep()) {
      case "decking":
        return {
          0: ["cyberdecks"],
          1: ["deck_modifications"],
          2: ["programs"]
        }
      case "rigging":
        return {
          0: ["vehicle_control_rigs"],
          1: ["vehicle_modifications"],
          2: ["drones"],
          3: ["vehicles"]
        }
      default:
        return {
          0: ["firearm", "armor", "modification", "melee", "projectile", "throwing"],
          1: ["headware", "earware", "eyeware", "bodyware", "cyberlimbs"],
          2: ["mobis", "biogenetic"],
          3: ["medical", "drugs", "electronics", "magical", "lifestyle"]
        }
    }
  }

  currentStep() {
    return this.currentStepValue || "gear"
  }

  initialCategoryCounts() {
    return this.hasInitialCategoryCountsValue ? this.initialCategoryCountsValue : {}
  }

  initialGroupCounts() {
    if (this.hasInitialGroupCountsValue) return this.initialGroupCountsValue

    return this.groupCountsFromCategories(this.initialCategoryCounts())
  }

  groupContentElement(groupIndex) {
    return this.element.querySelector(`[data-gear-selection-target="groupContent${groupIndex}"]`)
  }

  groupChevronElement(groupIndex) {
    return this.element.querySelector(`[data-gear-selection-target="groupChevron${groupIndex}"]`)
  }

  groupButtonElement(groupIndex) {
    return this.element.querySelector(`[data-group="${groupIndex}"]`)
  }

  categoryContentElement(category) {
    return this.element.querySelector(`[data-gear-selection-target="categoryContent${category}"]`)
  }

  categoryChevronElement(category) {
    return this.element.querySelector(`[data-gear-selection-target="categoryChevron${category}"]`)
  }

  categoryButtonElement(category) {
    return this.element.querySelector(`[data-category="${category}"]`)
  }
}
